import { useState } from "react";
import {
  ChartTooltip,
  tooltipStyles as tip,
  type ChartTooltipAnchor,
} from "../admin/chart/ChartTooltip";
import { axisTicks, bucketRangeLabel, bucketTimeLabel, niceAxisMax } from "../../lib/chartBuckets";
import { chartSeries, formatCount, stackBucket, type ToolUsage } from "../../lib/toolUsage";
import styles from "./ToolUsageChart.module.css";

const AXIS_W = 40;
const WIDTH = 640;
const HEIGHT = 180;
const BASELINE_PAD = 18;
const SEGMENT_GAP = 2;

type Hover = ChartTooltipAnchor & { readonly index: number };

const seriesClass = (slot: number | null): string => (slot === null ? styles.other : styles[`s${slot}`]) ?? "";

// Tool calls per bucket, stacked by app (busiest five + Other), with a legend
// so app identity never rests on colour alone.
export const ToolUsageChart = ({ usage }: { usage: ToolUsage }): JSX.Element => {
  const [hover, setHover] = useState<Hover | null>(null);
  const series = chartSeries(usage.apps);
  const stacks = usage.buckets.map((b) => stackBucket(b, series));
  const totals = stacks.map((s) => s.reduce((a, b) => a + b, 0));
  const axisMax = niceAxisMax(Math.max(...totals, 1));
  const usable = HEIGHT - BASELINE_PAD;
  const plotW = WIDTH - AXIS_W;
  const count = Math.max(usage.buckets.length, 1);
  const barGap = count > 40 ? 2 : 4;
  const barWidth = (plotW - barGap * (count - 1)) / count;
  const labelEvery = Math.ceil(count / 6);
  const hovered = hover === null ? undefined : usage.buckets[hover.index];

  const onEnter = (index: number) => (e: React.MouseEvent<SVGRectElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover({ index, cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };

  return (
    <div className={styles.wrap}>
      <ul className={styles.legend} aria-label="Apps">
        {series.map((s) => (
          <li key={s.key} className={styles.legendItem}>
            <span className={`${styles.swatch} ${seriesClass(s.slot)}`} aria-hidden />
            {s.name}
          </li>
        ))}
      </ul>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="Tool calls over time, by app"
        data-testid="tool-usage-chart"
      >
        {axisTicks(axisMax, 3).map((t) => {
          const y = usable - (t / axisMax) * (usable - 8);
          return (
            <g key={t} aria-hidden>
              <line className={styles.gridline} x1={AXIS_W} x2={WIDTH} y1={y} y2={y} />
              <text className={styles.axisLabel} x={AXIS_W - 6} y={y + 3} textAnchor="end">
                {formatCount(Math.round(t))}
              </text>
            </g>
          );
        })}
        {usage.buckets.map((bucket, i) => {
          const x = AXIS_W + i * (barWidth + barGap);
          let y = usable;
          return (
            <g key={bucket.t}>
              {stacks[i]!.map((value, s) => {
                if (value === 0) return null;
                const h = Math.max(1, (value / axisMax) * (usable - 8) - SEGMENT_GAP);
                y -= h + SEGMENT_GAP;
                return (
                  <rect
                    key={series[s]!.key}
                    className={seriesClass(series[s]!.slot)}
                    x={x}
                    y={y + SEGMENT_GAP}
                    width={barWidth}
                    height={h}
                    rx={Math.min(2, barWidth / 2)}
                  />
                );
              })}
              {i % labelEvery === 0 && (
                <text className={styles.axisLabel} x={x + barWidth / 2} y={HEIGHT - 4} textAnchor="middle" aria-hidden>
                  {bucketTimeLabel(bucket.t, usage.bucketSeconds)}
                </text>
              )}
              <rect
                className={styles.hit}
                x={x}
                y={0}
                width={barWidth + barGap}
                height={usable}
                aria-label={`${bucketRangeLabel(bucket.t, usage.bucketSeconds)}: ${formatCount(totals[i]!)} tool calls`}
                onMouseEnter={onEnter(i)}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          );
        })}
      </svg>
      {hover !== null && hovered !== undefined && (
        <ChartTooltip anchor={hover}>
          <div className={tip.tipHead}>
            <span className={tip.tipState}>{formatCount(totals[hover.index]!)} tool calls</span>
            <span className={tip.tipTime}>{bucketRangeLabel(hovered.t, usage.bucketSeconds)}</span>
          </div>
          <div className={tip.tipBody}>
            {series
              .map((s, i) => ({ s, n: stacks[hover.index]![i]! }))
              .filter(({ n }) => n > 0)
              .map(({ s, n }) => (
                <div key={s.key} className={styles.tipRow}>
                  <span className={`${styles.swatch} ${seriesClass(s.slot)}`} aria-hidden />
                  {s.name}: {formatCount(n)}
                </div>
              ))}
          </div>
        </ChartTooltip>
      )}
    </div>
  );
};
